import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiAnchor, FiCompass, FiSettings, FiUsers, FiTrendingUp, FiCheckCircle, FiClock, FiTarget, FiShield, FiTool, FiPlay, FiChevronRight, FiChevronDown, FiCalendar, FiBarChart3, FiArrowRight, FiZap, FiAward, FiArrowLeft, FiHelpCircle, FiSend, FiExternalLink, FiChevronUp } = FiIcons;

function App() {
  const [activePhase, setActivePhase] = useState(null);
  const [currentView, setCurrentView] = useState('discovery');
  const phaseRefs = useRef({});

  const phases = [
    {
      id: 1,
      title: 'Pre-Contract & Technical Specification',
      duration: 6,
      durationText: '~6 months',
      icon: FiCompass,
      color: '#3b82f6', // blue
      startMonth: 0,
      endMonth: 6,
      monthRange: 'Month 0-6',
      objective: 'Translate the owner\'s vision and mission profile into a preliminary General Arrangement (GA) and technical specification suitable for shipyard tendering.',
      governance: [
        'Management and technical team review the GA for operational flow, crew/service access, safety, and compliance.',
        'Iterative design reviews with the owner to confirm mission fit (range, toys, guest/crew numbers, tech level).'
      ],
      outcome: 'A clear, tender-ready GA and technical specification that minimize ambiguity in yard proposals.',
      activities: [
        'Owner brief and mission definition workshops',
        'Concept design and exterior styling iterations',
        'Preliminary GA (deck-by-deck layouts)',
        'Initial weight, range, and performance estimates',
        'Draft technical specification (hull, propulsion, key systems)',
        'Request-for-proposal package sent to shortlisted shipyards'
      ]
    },
    {
      id: 2,
      title: 'Contract, Basic Engineering & Procurement',
      duration: 4,
      durationText: '~4 months',
      icon: FiSettings,
      color: '#8b5cf6', // purple
      startMonth: 6,
      endMonth: 10,
      monthRange: 'Month 6-10',
      objective: 'Select the shipyard, agree the build contract, and complete basic engineering to start long-lead procurement.',
      governance: [
        'Detailed specification review to ensure workmanship standards (noise, vibration, technical quality) are clearly defined.',
        'Commercial and legal review of the build contract: price, change-order mechanisms, delivery, guarantees.'
      ],
      outcome: 'Signed Yacht & Construction Agreement. Authorization to begin basic engineering and order long-lead items.',
      activities: [
        'Evaluate yard proposals (technical, commercial, delivery slots)',
        'Yard visits and reference checks',
        'Refine spec based on the chosen yard\'s standards/platforms',
        'Basic naval architecture (hull form, stability, tank plan)',
        'Selection of main engines, generators, stabilizers, class and flag framework',
        'Placement of orders for long-lead equipment (engines, gearboxes, key systems)'
      ]
    },
    {
      id: 3,
      title: 'Detailed Design & Systems Integration',
      duration: 12,
      durationText: '~9–15 months',
      icon: FiTool,
      color: '#10b981', // emerald
      startMonth: 10,
      endMonth: 22,
      monthRange: 'Month 10-22',
      overlaps: [4],
      objective: 'Produce fully coordinated 3D engineering for structure and all systems, including HVAC, electrical, piping, propulsion, AV/IT, and automation, and secure class/flag approvals.',
      governance: [
        'Maintain a formal plan-approval log for owner, class, and flag comments and approvals.',
        'Regular design coordination meetings between yard, naval architect, interior designer, and owner\'s representatives.'
      ],
      outcome: 'Issued-for-construction drawings and integrated 3D models for hull, superstructure, systems, and interiors.',
      activities: [
        'Detailed structural engineering (bulkheads, decks, foundations)',
        '3D routing for pipes, cabling, and HVAC ducting',
        'Integration of propulsion and power management (including hybrid, if applicable)',
        'Automation and monitoring system architecture',
        'Interior design development (room layouts, joinery details, finishes, lighting)',
        'Class and flag plan approvals for structure and systems'
      ]
    },
    {
      id: 4,
      title: 'Fabrication & Hull Construction',
      duration: 15,
      durationText: '~12–18 months',
      icon: FiShield,
      color: '#f59e0b', // amber
      startMonth: 16,
      endMonth: 31,
      monthRange: 'Month 16-31',
      overlaps: [3, 5],
      objective: 'Fabricate and assemble hull and superstructure, install primary machinery, and establish the yacht\'s engineering backbone.',
      governance: [
        'Bi‑monthly yard attendance by the Project Manager and Captain to audit welding, fabrication quality, and compliance with drawings.',
        'Class surveys at key structural milestones (keel laying, hull completion, superstructure join).'
      ],
      outcome: 'Structurally complete hull and superstructure with main engines, generators, and major foundations installed.',
      activities: [
        'Steel/aluminum cutting and panel fabrication',
        'Hull and superstructure block assembly and joining',
        'Installation of main engines, gearboxes, and generators',
        'Installation of stabilizers and major tank structures',
        'Initial routing of major trunking, cable trays, and primary piping'
      ]
    },
    {
      id: 5,
      title: 'Outfitting, Interior & Commissioning',
      duration: 10,
      durationText: '~9–12 months',
      icon: FiUsers,
      color: '#ef4444', // red
      startMonth: 25,
      endMonth: 35,
      monthRange: 'Month 25-35',
      objective: 'Complete interior and exterior outfitting and fully integrate AV/IT, automation, and hotel systems, leading to dockside commissioning.',
      governance: [
        'Specialized acoustics and vibration audits by independent experts.',
        'Structured Factory Acceptance Tests (FATs) and Harbour Acceptance Tests (HATs) for all major systems.'
      ],
      outcome: 'All interiors and technical systems completed and tested, ready for sea trials.',
      activities: [
        'Interior fit-out (guest cabins, owner\'s areas, crew areas, galley)',
        'Exterior outfitting (deck furniture, railings, glazing, paint)',
        'Installation and configuration of AV/IT systems and smart controls',
        'HVAC balancing and comfort tuning',
        'Dockside testing of power, plumbing, alarms, safety, and automation systems'
      ]
    },
    {
      id: 6,
      title: 'Sea Trials, Handover & Operation',
      duration: 4,
      durationText: '~3–6 months',
      icon: FiAnchor,
      color: '#06b6d4', // cyan
      startMonth: 35,
      endMonth: 39,
      monthRange: 'Month 35-39',
      objective: 'Verify performance and safety at sea, train crew, and complete formal handover into operational management.',
      governance: [
        'Agreed sea trial program with owner, yard, and class.',
        'Final delivery protocols, warranty list, and issuance of full-term class and flag certificates.'
      ],
      outcome: 'Yacht accepted by the owner, with warranties in place and crew ready for regular operations.',
      activities: [
        'Speed, maneuvering, range, and endurance sea trials',
        'Noise and vibration measurements in realistic operating conditions',
        'Safety and emergency drills with full crew',
        'Punch list rectification and final sign-offs',
        'Handover of documentation, manuals, and digital models (if provided)'
      ]
    }
  ];

  const totalDuration = 39; // months

  const handlePhaseClick = (phaseId) => {
    setActivePhase(phaseId);
    setTimeout(() => {
      const element = phaseRefs.current[`phase-${phaseId}`];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const scrollToTimeline = () => {
    setActivePhase(null);
    const element = phaseRefs.current['timeline'];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* Header */}
      <header className="border-b border-slate-800/50 bg-slate-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiAnchor} className="text-2xl text-cyan-400" />
              <h1 className="text-xl sm:text-2xl font-light text-white tracking-wide">
                Superyacht Build Roadmap
              </h1>
            </div>
            <div className="flex space-x-1 bg-slate-900/50 backdrop-blur-sm rounded-lg p-1 w-full sm:w-auto border border-slate-800/30">
              <button
                onClick={() => setCurrentView('discovery')}
                className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-md text-sm font-light transition-all ${
                  currentView === 'discovery' 
                    ? 'bg-slate-800 text-white' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <span className="hidden sm:inline">Strategic Discovery</span>
                <span className="sm:hidden">Discovery</span>
              </button>
              <button
                onClick={() => setCurrentView('roadmap')}
                className={`flex-1 sm:flex-initial px-3 sm:px-4 py-2 rounded-md text-sm font-light transition-all ${
                  currentView === 'roadmap' 
                    ? 'bg-slate-800 text-white' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                <span className="hidden sm:inline">Project Timeline</span>
                <span className="sm:hidden">Timeline</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <AnimatePresence mode="wait">
          {currentView === 'discovery' ? (
            <motion.div
              key="discovery"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <DiscoveryPhase />
            </motion.div>
          ) : (
            <motion.div
              key="roadmap"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ProjectTimeline 
                phases={phases} 
                activePhase={activePhase} 
                setActivePhase={handlePhaseClick} 
                totalDuration={totalDuration}
                phaseRefs={phaseRefs}
                scrollToTimeline={scrollToTimeline}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

function DiscoveryPhase() {
  const [expandedSection, setExpandedSection] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizComplete, setQuizComplete] = useState(false);

  const sections = [
    {
      id: 'mission',
      title: 'Mission Profile Definition',
      icon: FiTarget,
      color: '#3b82f6',
      items: [
        'Primary usage patterns (charter, private, expedition)',
        'Geographic operating regions and seasonal preferences',
        'Guest capacity and typical group compositions',
        'Crew requirements and operational preferences',
        'Range and endurance specifications',
        'Speed profile and fuel efficiency targets'
      ]
    },
    {
      id: 'architecture',
      title: 'Architectural Framework',
      icon: FiCompass,
      color: '#10b981',
      items: [
        'Overall length and beam constraints',
        'Draft limitations for preferred destinations',
        'Deck configuration and flow optimization',
        'Interior volume allocation and privacy zones',
        'Technical spaces and accessibility requirements',
        'Compliance framework (MCA, Flag State, Class)'
      ]
    }
  ];

  const guidingQuestions = [
    {
      id: 1,
      question: "Do you need twin-engine medium helicopters with certified night operations, or are single-engine light helicopters with touch-and-go landings sufficient?",
      analysis: [
        "If single-engine light helicopters + touch-and-go meet your needs: a 60m works fine.",
        "If you require twin-engine medium helicopters + certified night helipad: a 75m is required."
      ]
    },
    {
      id: 2,
      question: "What matters more to you in the Med: Being physically on the quay in the smaller/older basins when possible, or accepting that in iconic spots you will often anchor and tender in, regardless of size?",
      analysis: [
        "If \"being on the quay as often as possible\" is a big deal: you get a relative advantage with 60m.",
        "If anchoring/tendering is acceptable and you care more about onboard space: 75m stays fully viable."
      ]
    },
    {
      id: 3,
      question: "Do you want easier access to busy marinas and popular spots where smaller yachts often get priority, or are you fine with anchoring out?",
      analysis: [
        "If \"easier marina access\" matters: that leans toward a 60m.",
        "If you are \"fine anchoring out when needed\": 75m remains fully viable."
      ]
    },
    {
      id: 4,
      question: "Do you want fixed, always-there rooms (permanent schoolroom, dedicated gym, treatment room, proper office), or are you comfortable with multi-use spaces?",
      analysis: [
        "Strong preference for fixed rooms: easier to achieve cleanly on a 75m.",
        "Comfortable with multi-use/convertible spaces: a 60m is often enough."
      ]
    },
    {
      id: 5,
      question: "How much of the yacht's internal volume are you willing to dedicate to garages and technical spaces for tenders, toys, and gear, versus beach club and interior guest areas?",
      analysis: [
        "If you are comfortable with disciplined volume allocation (every extra toy clearly trades off against something else): that aligns well with a 60m.",
        "If you want more space for toys/gear so those trade-offs are less: that naturally points toward a 75m."
      ]
    },
    {
      id: 6,
      question: "What matters more: a leaner, denser boat that still does 10–14 days off-grid comfortably, or a larger one where 20–30 days off-grid is a realistic design target?",
      analysis: [
        "If 10–14 days autonomy is enough: a 60m is usually sufficient.",
        "If you really want to design for 20–30 days off-grid as a regular pattern: that starts to justify a 75m."
      ]
    },
    {
      id: 7,
      question: "How many permanent crew do you actually want around you in day-to-day life?",
      analysis: [
        "If your comfort zone is ~12–15 crew: that aligns naturally with a 60m.",
        "If you are comfortable with 20+ crew and a more structured shipboard environment: a 75m makes sense."
      ]
    },
    {
      id: 8,
      question: "If you add tutors, nannies, wellness staff, etc., do you want them fully integrated into the main crew/guest ecosystem, or do you prefer keeping the onboard headcount tighter so the boat still feels intimate?",
      analysis: [
        "If you want to keep the onboard environment compact: that is more natural on a 60m.",
        "If you are happy to absorb specialists on the main yacht without it feeling crowded: that points toward a 75m."
      ]
    },
    {
      id: 9,
      question: "Do you want a distinct \"owner/family deck\" clearly separated from guest and crew traffic, or is a well-designed but less separated arrangement acceptable?",
      analysis: [
        "Strong desire for a true owner deck: easier and cleaner on a 75m.",
        "Comfortable with more integrated layouts where family spaces are well designed but not on their own deck: a 60m is fine."
      ]
    },
    {
      id: 10,
      question: "Are you willing to carry more complex systems (larger hybrid plant, bigger hotel load, more IT) with the specialist crew to match, or do you prefer keeping the technical side more simple if possible?",
      analysis: [
        "If you prefer technical simplicity and easier troubleshooting: that leans toward a 60m (or a simpler spec 75m).",
        "If you are OK with more complexity + dedicated tech crew (ETO, advanced engineering): a 75m gives more room to exploit that."
      ]
    },
    {
      id: 11,
      question: "Do you want it to feel more like a tight team with less hierarchy, or more like a small ship with clearer departments and procedures?",
      analysis: [
        "Preference for tight team, fewer layers of hierarchy: more natural on a 60m.",
        "Comfortable with \"ship feel\", clear hierarchy, more structure: more natural on a 75m."
      ]
    },
    {
      id: 12,
      question: "If you had to pick one pattern, is it mostly Bahamas/Caribbean with some Med seasons, or a global pattern including longer crossings and more remote regions?",
      analysis: [
        "Mostly Bahamas/Caribbean + Med with typical seasonal routes: either works, with a slight practicality edge to 60m for access.",
        "A global pattern with regular remote/high-latitude cruising: a 75m is easier to justify for range and capacity."
      ]
    },
    {
      id: 13,
      question: "Ignoring resale for a moment, are you more concerned about capital and running cost, or about the experience, onboard amenities, and capability?",
      analysis: [
        "If the cost gradient between 60m and 75m matters and you want to control spend: that pushes toward a 60m.",
        "If your attitude is that the experience and capabilities of the platform justify the 75m premium: 75m remains viable."
      ]
    },
    {
      id: 14,
      question: "Do you want everything guest-facing (spa, gym, cinema/media space, proper office, serious beach club) fully built-in on day one, or are you comfortable with some functions being lighter, combined, or added later?",
      analysis: [
        "If you want a complete, self-contained guest universe with fewer compromises and less multi-use: that is easier to achieve cleanly on a 75m.",
        "If you are happy with hybrid/multi-use spaces and can live without every feature being standalone: a 60m can be perfectly adequate."
      ]
    },
    {
      id: 15,
      question: "How much future flexibility do you want for adding or upgrading systems (extra batteries, equipment, different toys) without the boat starting to feel \"maxed out\"?",
      analysis: [
        "If you want real headroom to add gear and systems over time without giving up guest space, that margin is easier to find on a 75m.",
        "If you are comfortable treating the build as closer to \"fixed\" (most big choices locked at delivery, with smaller upgrades later): a 60m can be tightly optimized around your initial brief."
      ]
    },
    {
      id: 16,
      question: "How much do you want to prioritize crew living standards versus maximizing guest space?",
      analysis: [
        "If standard individual crew cabins with good crew spaces are sufficient: that fits well on a 60m.",
        "If you want larger crew cabins and a better crew mess/gym without noticeably eating into guest volume: the extra length and GT of a 75m make that easier."
      ]
    }
  ];

  const questions = [
    {
      id: 1,
      question: "Do you need twin-engine medium helicopters with certified night operations, or are single-engine light helicopters with touch-and-go landings sufficient?",
      options: [
        { text: "Single-engine light helis + touch-and-go OK", type: "60m" },
        { text: "Twin-engine medium helis + certified night ops", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 2,
      question: "What matters more to you in the Med: Being physically on the quay in the smallest/oldest basins when possible, or accepting that in iconic spots you will often anchor and tender in, regardless of size?",
      options: [
        { text: "Quay access in small basins important", type: "60m" },
        { text: "Anchoring/tendering acceptable", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 3,
      question: "Do you want easier access to busy marinas and popular spots where smaller yachts often get priority, or are you fine with occasionally anchoring out while bigger yachts coordinate docking?",
      options: [
        { text: "Want easier marina access", type: "60m" },
        { text: "Fine anchoring out when needed", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 4,
      question: "Do you want fixed, always-there rooms (permanent schoolroom, dedicated gym, treatment room, proper office), or are you comfortable with multi-use spaces?",
      options: [
        { text: "Multi-use/convertible spaces work", type: "60m" },
        { text: "Need fixed dedicated rooms", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 5,
      question: "60m requires more disciplined volume allocation (tenders/garage vs beach club vs interior). 75m offers more space for larger tenders/more toys without hard trade-offs.",
      options: [
        { text: "Disciplined volume allocation OK", type: "60m" },
        { text: "Want more space for toys/gear", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 6,
      question: "What matters more: A leaner, denser boat that still does 10–14 days off-grid comfortably, or a larger one where 20–30 days off-grid is a realistic design target?",
      options: [
        { text: "10-14 days autonomy is enough", type: "60m" },
        { text: "Want 20-30 days off-grid capability", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 7,
      question: "How many permanent crew do you actually want onboard?",
      options: [
        { text: "Comfortable with ~12-15 crew", type: "60m" },
        { text: "OK with 20+ crew environment", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 8,
      question: "If you add tutors, nannies, wellness staff, etc., do you want them fully integrated in the main crew/guest ecosystem, or kept to a reasonable number so the boat still feels intimate?",
      options: [
        { text: "Keep onboard environment compact", type: "60m" },
        { text: "Absorb specialists on main yacht", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 9,
      question: "Do you want a distinct \"owner/family deck\" clearly separated from guest and crew traffic, or is a well-designed but less separated arrangement acceptable?",
      options: [
        { text: "Integrated layouts acceptable", type: "60m" },
        { text: "True owner deck required", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 10,
      question: "Are you willing to carry more complex systems (larger hybrid plant, bigger hotel load, more IT) with the crew to match, or do you prefer keeping the technical side as simple as possible?",
      options: [
        { text: "Prefer technical simplicity", type: "60m" },
        { text: "OK with more complexity +", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 11,
      question: "Do you want it to feel more like a tight team with less hierarchy, or a small ship with more structure and procedures?",
      options: [
        { text: "Want tight team, less hierarchy", type: "60m" },
        { text: "Ship feel with clear structure OK", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 12,
      question: "If you had to pick one pattern: Mostly Bahamas/Caribbean + some Med seasons, or global pattern including longer crossings and remote regions?",
      options: [
        { text: "Mostly Bahamas/Caribbean + Med", type: "60m" },
        { text: "Global with remote/high-latitude", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 13,
      question: "Ignoring resale: Are you more concerned about capital and running cost, or about the experience, onboard amenities, and capability?",
      options: [
        { text: "Cost gradient between sizes matters", type: "60m" },
        { text: "Experience justifies 75m premium", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 14,
      question: "Do you want everything guest-facing (spa, gym, cinema/media space, proper office, serious beach club) fully built-in on day one, or are you comfortable with some functions being lighter, combined, or added later?",
      options: [
        { text: "Hybrid/multi-use spaces adequate", type: "60m" },
        { text: "Complete self-contained guest universe", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 15,
      question: "How much future flexibility do you want for adding/upgrading systems (extra batteries, equipment, different toys) without the boat feeling \"maxed out\"?",
      options: [
        { text: "Fixed build with smaller upgrades", type: "60m" },
        { text: "Headroom for future system additions", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    },
    {
      id: 16,
      question: "How much do you want to prioritize crew living standards versus maximizing guest space?",
      options: [
        { text: "Standard individual crew cabins sufficient", type: "60m" },
        { text: "Larger crew cabins + better crew mess/gym", type: "75m" },
        { text: "Neutral", type: "neutral" }
      ]
    }
  ];

  const handleAnswer = (type) => {
    const newAnswers = [...answers, type];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setQuizComplete(true);
    }
  };

  const calculateResult = () => {
    const score60m = answers.filter(a => a === '60m').length;
    const score75m = answers.filter(a => a === '75m').length;
    const neutralCount = answers.filter(a => a === 'neutral').length;

    if (score60m >= 12) return { type: '60m Explorer', strength: 'Strong', description: '~1,000–1,200 GT - Medium-size global yacht. More flexible for port access and marginal draft situations; tighter volume and crew.' };
    if (score75m >= 12) return { type: '75m Explorer', strength: 'Strong', description: '~2,000–2,500 GT - Larger-volume global yacht. More space for dedicated rooms/systems; more formal operation; slightly deeper draft/port constraints.' };
    if (score60m > score75m + 2) return { type: '60m Explorer', strength: 'Moderate', description: '~1,000–1,200 GT - Medium-size global yacht. More flexible for port access and marginal draft situations; tighter volume and crew.' };
    if (score75m > score60m + 2) return { type: '75m Explorer', strength: 'Moderate', description: '~2,000–2,500 GT - Larger-volume global yacht. More space for dedicated rooms/systems; more formal operation; slightly deeper draft/port constraints.' };
    return { 
      type: '60m + 40-60m Support', 
      strength: 'Split Decision', 
      description: '~1,500–2,000 GT across 2 hulls - 40–60m Support (499–800 GT) serious workhorse explorer. Carries chase boats, submersibles, dive gear, cold storage/processing, extra toys, mission crew. Pairs with 60m mothership. Doubled ops complexity.' 
    };
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setQuizComplete(false);
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="text-center mb-8 sm:mb-12">
        <motion.h2 
          className="text-3xl sm:text-4xl font-light text-white mb-4 tracking-wide"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          Phase 0: Strategic Discovery
        </motion.h2>
        <motion.p 
          className="text-lg sm:text-xl text-slate-400 max-w-3xl mx-auto px-4 font-light"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          Establishing the foundational parameters that will guide every subsequent decision in your superyacht build journey.
        </motion.p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
        {sections.map((section, index) => (
          <motion.div
            key={section.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + index * 0.1 }}
            className="bg-slate-900/30 backdrop-blur-sm rounded-xl border border-slate-800/50 overflow-hidden hover:border-slate-700/50 transition-colors"
          >
            <button
              onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
              className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-800/20 transition-colors"
            >
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="p-2 sm:p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                  <SafeIcon icon={section.icon} className="text-lg sm:text-xl" style={{ color: section.color }} />
                </div>
                <div className="text-left flex-1">
                  <h3 className="text-lg sm:text-xl font-light text-white">{section.title}</h3>
                  <p className="text-sm text-slate-500 mt-1 font-light">
                    {section.items.length} key considerations
                  </p>
                </div>
              </div>
              <SafeIcon 
                icon={expandedSection === section.id ? FiChevronDown : FiChevronRight} 
                className="text-slate-500 transition-transform flex-shrink-0" 
              />
            </button>
            
            <AnimatePresence>
              {expandedSection === section.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t border-slate-800/30"
                >
                  <div className="p-4 sm:p-6 space-y-3">
                    {section.items.map((item, itemIndex) => (
                      <motion.div
                        key={itemIndex}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: itemIndex * 0.05 }}
                        className="flex items-start space-x-3"
                      >
                        <SafeIcon icon={FiCheckCircle} className="mt-0.5 flex-shrink-0" style={{ color: section.color }} />
                        <span className="text-slate-300 text-sm sm:text-base font-light">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      {/* Guiding Questions Section - Collapsed */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-slate-900/30 backdrop-blur-sm rounded-xl border border-slate-800/50 overflow-hidden hover:border-slate-700/50 transition-colors"
      >
        <button
          onClick={() => setExpandedSection(expandedSection === 'guiding' ? null : 'guiding')}
          className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-800/20 transition-colors"
        >
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <SafeIcon icon={FiHelpCircle} className="text-lg sm:text-xl text-orange-400" />
            </div>
            <div className="text-left flex-1">
              <h3 className="text-lg sm:text-xl font-light text-white">Guiding Questions</h3>
              <p className="text-sm text-slate-500 mt-1 font-light">
                Detailed analysis to determine your ideal yacht configuration
              </p>
            </div>
          </div>
          <SafeIcon 
            icon={expandedSection === 'guiding' ? FiChevronDown : FiChevronRight} 
            className="text-slate-500 transition-transform flex-shrink-0" 
          />
        </button>

        <AnimatePresence>
          {expandedSection === 'guiding' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t border-slate-800/30"
            >
              <div className="p-6 sm:p-8 space-y-8">
                {/* Yacht Size Options */}
                <div className="space-y-6">
                  <h4 className="text-xl font-light text-white mb-4">Yacht Size Options to Consider</h4>
                  
                  <div className="grid gap-4">
                    <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
                      <h5 className="text-lg font-light text-blue-400 mb-2">60m (~1,000–1,200 GT)</h5>
                      <p className="text-slate-300 text-sm font-light">Medium-size global yacht. More flexible for port access but with less endurance, volume, and crew.</p>
                    </div>
                    
                    <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
                      <h5 className="text-lg font-light text-purple-400 mb-2">75m (~2,000–2,500 GT)</h5>
                      <p className="text-slate-300 text-sm font-light">Larger-volume global yacht. More space for dedicated rooms, additional crew; greater capabilities and endurance.</p>
                    </div>
                    
                    <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
                      <h5 className="text-lg font-light text-green-400 mb-2">40–60m Support (499–800 GT)</h5>
                      <p className="text-slate-300 text-sm font-light">Dedicated support vessel; carries chase boats, submersibles, dive gear, cold storage/processing, extra toys, mission crew. Pairs with 60m mothership. Increases ops complexity; but significantly increases capabilities; total ~1,500–2,000 GT across 2 hulls.</p>
                    </div>
                  </div>
                </div>

                {/* Guiding Questions */}
                <div className="space-y-4">
                  <h4 className="text-xl font-light text-white mb-4">Guiding Questions</h4>
                  
                  {guidingQuestions.map((q, index) => (
                    <motion.div
                      key={q.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-slate-800/20 rounded-lg p-4 border border-slate-700/30 space-y-3"
                    >
                      <div className="flex items-start space-x-3">
                        <span className="text-orange-400 font-light text-sm mt-1">Q{q.id}</span>
                        <div className="flex-1">
                          <h5 className="text-white font-light mb-2">{q.question}</h5>
                          <div className="space-y-2">
                            {q.analysis.map((point, pointIndex) => (
                              <div key={pointIndex} className="flex items-start space-x-2">
                                <span className="text-slate-500 text-sm mt-1">•</span>
                                <p className="text-slate-400 text-sm font-light">{point}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}

                  {/* Collapse button at the end of guiding questions */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="pt-6 border-t border-slate-700/30"
                  >
                    <button
                      onClick={() => setExpandedSection(null)}
                      className="mx-auto flex items-center space-x-2 px-4 py-2 bg-slate-800/30 border border-slate-700/50 rounded-lg hover:bg-slate-800/50 transition-colors text-slate-400 hover:text-white"
                    >
                      <SafeIcon icon={FiChevronUp} className="text-sm" />
                      <span className="text-sm font-light">Collapse section</span>
                    </button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Interactive Questions Section - Collapsed */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-slate-900/30 backdrop-blur-sm rounded-xl border border-slate-800/50 overflow-hidden hover:border-slate-700/50 transition-colors"
      >
        <button
          onClick={() => setExpandedSection(expandedSection === 'quiz' ? null : 'quiz')}
          className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-800/20 transition-colors"
        >
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="p-2 sm:p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
              <SafeIcon icon={FiHelpCircle} className="text-lg sm:text-xl text-indigo-400" />
            </div>
            <div className="text-left flex-1">
              <h3 className="text-lg sm:text-xl font-light text-white">Interactive Questions</h3>
              <p className="text-sm text-slate-500 mt-1 font-light">
                Answer {questions.length} questions to determine your ideal yacht size
              </p>
            </div>
          </div>
          <SafeIcon 
            icon={expandedSection === 'quiz' ? FiChevronDown : FiChevronRight} 
            className="text-slate-500 transition-transform flex-shrink-0" 
          />
        </button>

        <AnimatePresence>
          {expandedSection === 'quiz' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t border-slate-800/30"
            >
              <div className="p-6 sm:p-8">
                {/* Quiz Header inside content */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl sm:text-3xl font-light text-white mb-4">Discovery Quiz</h3>
                  <p className="text-slate-400 max-w-2xl mx-auto font-light">
                    Select the option that best matches your preferences
                  </p>
                </div>

                {!quizComplete ? (
                  <div className="max-w-4xl mx-auto">
                    {/* Progress Bar */}
                    <div className="mb-8">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-slate-400 font-light">Question {currentQuestion + 1} of {questions.length}</span>
                        <span className="text-sm text-slate-400 font-light">{Math.round(((currentQuestion + 1) / questions.length) * 100)}%</span>
                      </div>
                      <div className="w-full bg-slate-800/50 rounded-full h-2">
                        <motion.div
                          className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                    </div>

                    {/* Question Card */}
                    <motion.div
                      key={currentQuestion}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      className="bg-slate-800/30 rounded-xl border border-slate-700/50 p-6 sm:p-8 mb-8"
                    >
                      <h4 className="text-xl sm:text-2xl font-light text-white mb-8 leading-relaxed">
                        {questions[currentQuestion].question}
                      </h4>

                      {/* Answer Options */}
                      <div className="space-y-4">
                        {questions[currentQuestion].options.map((option, index) => (
                          <motion.button
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 + index * 0.1 }}
                            onClick={() => handleAnswer(option.type)}
                            className={`w-full text-left p-4 sm:p-6 rounded-lg border transition-all hover:scale-[1.02] ${
                              option.type === '60m' 
                                ? 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20 hover:border-blue-500/50'
                                : option.type === '75m'
                                ? 'bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20 hover:border-purple-500/50'
                                : 'bg-slate-700/30 border-slate-600/50 hover:bg-slate-700/50 hover:border-slate-600/70'
                            }`}
                          >
                            <span className="text-white text-sm sm:text-base font-light">
                              {option.text}
                            </span>
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  </div>
                ) : (
                  /* Results */
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-4xl mx-auto text-center"
                  >
                    <SafeIcon icon={FiSend} className="text-5xl text-green-400 mx-auto mb-6" />
                    <h3 className="text-3xl sm:text-4xl font-light text-white mb-6">Your Results</h3>
                    
                    <div className="bg-slate-800/30 rounded-xl border border-slate-700/50 p-8 mb-8">
                      <h4 className="text-2xl font-light text-white mb-4">{calculateResult().type}</h4>
                      <div className="text-lg text-slate-400 mb-2">{calculateResult().strength}</div>
                      <p className="text-slate-300 font-light leading-relaxed">{calculateResult().description}</p>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-4 mb-8">
                      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                        <div className="text-2xl font-light text-blue-400 mb-1">{answers.filter(a => a === '60m').length}</div>
                        <div className="text-sm text-slate-400 font-light">60m Explorer</div>
                      </div>
                      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                        <div className="text-2xl font-light text-purple-400 mb-1">{answers.filter(a => a === '75m').length}</div>
                        <div className="text-sm text-slate-400 font-light">75m Explorer</div>
                      </div>
                      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                        <div className="text-2xl font-light text-slate-400 mb-1">{answers.filter(a => a === 'neutral').length}</div>
                        <div className="text-sm text-slate-400 font-light">Neutral</div>
                      </div>
                    </div>

                    <button
                      onClick={resetQuiz}
                      className="px-6 py-3 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition-colors font-light"
                    >
                      Retake Quiz
                    </button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-slate-900/40 backdrop-blur-sm rounded-xl border border-slate-800/50 p-6 sm:p-8 text-center"
      >
        <SafeIcon icon={FiZap} className="text-3xl text-yellow-400 mx-auto mb-4" />
        <h3 className="text-2xl font-light text-white mb-3"> Strategic Foundation</h3>
        <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed px-4 font-light">
          This discovery phase ensures alignment between vision and execution, establishing clear parameters 
          that minimize costly changes during the build process and maximize operational satisfaction.
        </p>
      </motion.div>
    </div>
  );
}

function ProjectTimeline({ phases, activePhase, setActivePhase, totalDuration, phaseRefs, scrollToTimeline }) {
  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <motion.h2 
          className="text-4xl sm:text-5xl font-light text-white mb-4 tracking-wide"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Project Timeline
        </motion.h2>
        <motion.p 
          className="text-xl text-slate-400 max-w-3xl mx-auto mb-8 px-4 font-light"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          Complete 39-month journey from concept to delivery
        </motion.p>

        {/* Progress Overview */}
        <motion.div 
          className="inline-flex items-center space-x-8 bg-slate-900/40 backdrop-blur-sm rounded-2xl border border-slate-800/50 px-8 py-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="text-center">
            <div className="text-3xl font-light text-white mb-1">{totalDuration}</div>
            <div className="text-sm text-slate-500 font-light">Months</div>
          </div>
          <div className="w-px h-12 bg-slate-700"></div>
          <div className="text-center">
            <div className="text-3xl font-light text-white mb-1">6</div>
            <div className="text-sm text-slate-500 font-light">Phases</div>
          </div>
          <div className="w-px h-12 bg-slate-700"></div>
          <div className="text-center">
            <div className="text-3xl font-light text-white mb-1">2029</div>
            <div className="text-sm text-slate-500 font-light">Delivery</div>
          </div>
        </motion.div>
      </div>

      {/* Vertical Timeline */}
      <div ref={(el) => phaseRefs.current['timeline'] = el} className="relative mb-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Timeline Column */}
          <div className="lg:w-1/4">
            <div className="relative">
              {/* Vertical Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-slate-800"></div>
              
              {/* Timeline Nodes */}
              <div className="space-y-8">
                {phases.map((phase, index) => (
                  <motion.div
                    key={phase.id}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="relative flex items-start space-x-4"
                  >
                    {/* Node Circle */}
                    <div className="relative z-10">
                      <div className="absolute inset-0 bg-slate-800 rounded-full blur-xl opacity-50"></div>
                      <div 
                        className={`relative w-16 h-16 rounded-full border-2 flex items-center justify-center transition-all cursor-pointer bg-slate-950 ${
                          activePhase === phase.id ? 'border-white scale-110' : 'border-slate-700 hover:scale-105'
                        }`}
                        style={{ 
                          borderColor: activePhase === phase.id ? phase.color : undefined,
                          boxShadow: activePhase === phase.id ? `0 0 20px ${phase.color}40` : 'none'
                        }}
                        onClick={() => setActivePhase(phase.id)}
                      >
                        <SafeIcon 
                          icon={phase.icon} 
                          className={`text-xl transition-colors ${
                            activePhase === phase.id ? 'text-white' : ''
                          }`}
                          style={{ color: activePhase === phase.id ? 'white' : phase.color }}
                        />
                      </div>
                    </div>
                    
                    {/* Phase Info */}
                    <div className="flex-1 pt-2">
                      <div className="flex flex-col">
                        <span className="text-sm font-light text-slate-400">Phase {phase.id}</span>
                        <span className="text-xs text-slate-500">{phase.monthRange}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Content Column */}
          <div className="lg:w-3/4 space-y-6">
            {phases.map((phase, index) => (
              <motion.div
                key={phase.id}
                ref={(el) => phaseRefs.current[`phase-${phase.id}`] = el}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative group cursor-pointer transition-all duration-300 ${
                  activePhase === phase.id ? 'scale-105' : 'hover:scale-[1.02]'
                }`}
                onClick={() => setActivePhase(activePhase === phase.id ? null : phase.id)}
              >
                <div className="absolute inset-0 bg-gradient-to-r opacity-10 rounded-xl blur-xl"></div>
                <div 
                  className={`relative bg-slate-900/40 backdrop-blur-sm rounded-xl border transition-all duration-300 ${
                    activePhase === phase.id 
                      ? 'border-white/20 shadow-lg' 
                      : 'border-slate-800/50 hover:border-slate-700/50'
                  }`}
                  style={{ borderColor: activePhase === phase.id ? phase.color + '40' : undefined }}
                >
                  {/* Phase Header */}
                  <div className="p-6 sm:p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: phase.color + '20', border: `1px solid ${phase.color}40` }}
                        >
                          <SafeIcon icon={phase.icon} className="text-xl" style={{ color: phase.color }} />
                        </div>
                        <div>
                          <div className="flex items-center space-x-3 mb-2">
                            <span className="text-sm font-light" style={{ color: phase.color }}>Phase {phase.id}</span>
                            <span className="text-xs px-2 py-1 rounded-full bg-slate-800/50 text-slate-400 border border-slate-700/30">
                              {phase.durationText}
                            </span>
                          </div>
                          <h3 className="text-xl sm:text-2xl font-light text-white">{phase.title}</h3>
                          <div className="text-sm text-slate-500 font-light mt-1">
                            {phase.monthRange}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {phase.overlaps && (
                          <span className="text-xs px-2 py-1 rounded-full bg-slate-800/30 text-slate-400 border border-slate-700/30">
                            Overlaps {phase.overlaps.join(', ')}
                          </span>
                        )}
                        {activePhase === phase.id && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              scrollToTimeline();
                            }}
                            className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/50 hover:bg-slate-800/70 transition-colors"
                          >
                            <SafeIcon icon={FiArrowLeft} className="text-slate-300" />
                          </button>
                        )}
                        <SafeIcon 
                          icon={activePhase === phase.id ? FiChevronDown : FiArrowRight} 
                          className="text-slate-400 transition-transform" 
                        />
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="text-center p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                        <div className="text-lg font-light text-white">{phase.duration}</div>
                        <div className="text-xs text-slate-500 font-light">Months</div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                        <div className="text-lg font-light text-white">{phase.activities.length}</div>
                        <div className="text-xs text-slate-500 font-light">Activities</div>
                      </div>
                      <div className="text-center p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                        <div className="text-lg font-light text-white">M{phase.startMonth}</div>
                        <div className="text-xs text-slate-500 font-light">Start</div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    <AnimatePresence>
                      {activePhase === phase.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="border-t border-slate-800/30 pt-6 space-y-6"
                        >
                          <div>
                            <h4 className="text-lg font-light text-slate-300 mb-3 flex items-center">
                              <SafeIcon icon={FiTarget} className="mr-2" style={{ color: phase.color }} />
                              Objective
                            </h4>
                            <p className="text-slate-400 leading-relaxed font-light">{phase.objective}</p>
                          </div>

                          <div>
                            <h4 className="text-lg font-light text-slate-300 mb-3 flex items-center">
                              <SafeIcon icon={FiShield} className="mr-2" style={{ color: phase.color }} />
                              Governance
                            </h4>
                            <ul className="space-y-2">
                              {phase.governance.map((item, index) => (
                                <motion.li
                                  key={index}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: index * 0.05 }}
                                  className="flex items-start space-x-3"
                                >
                                  <SafeIcon icon={FiCheckCircle} className="mt-1 flex-shrink-0" style={{ color: phase.color }} />
                                  <span className="text-slate-400 text-sm sm:text-base font-light">{item}</span>
                                </motion.li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <h4 className="text-lg font-light text-slate-300 mb-3 flex items-center">
                              <SafeIcon icon={FiTrendingUp} className="mr-2" style={{ color: phase.color }} />
                              Outcome
                            </h4>
                            <p className="text-slate-400 leading-relaxed font-light">{phase.outcome}</p>
                          </div>

                          <div>
                            <h4 className="text-lg font-light text-slate-300 mb-3 flex items-center">
                              <SafeIcon icon={FiPlay} className="mr-2" style={{ color: phase.color }} />
                              Key Activities
                            </h4>
                            <div className="grid sm:grid-cols-2 gap-3">
                              {phase.activities.map((activity, index) => (
                                <motion.div
                                  key={index}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: index * 0.03 }}
                                  className="flex items-start space-x-3 p-3 rounded-lg bg-slate-800/20 border border-slate-700/30"
                                >
                                  <SafeIcon icon={FiClock} className="mt-0.5 flex-shrink-0" style={{ color: phase.color }} />
                                  <span className="text-slate-400 text-sm font-light">{activity}</span>
                                </motion.div>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Project Summary */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-slate-900/40 backdrop-blur-sm rounded-xl border border-slate-800/50 p-8 text-center"
      >
        <SafeIcon icon={FiAward} className="text-4xl text-yellow-400 mx-auto mb-6" />
        <h3 className="text-3xl font-light text-white mb-8">Project Complete</h3>
        <div className="grid sm:grid-cols-3 gap-8">
          <div>
            <div className="text-4xl font-light text-white mb-2">{totalDuration} Months</div>
            <div className="text-sm text-slate-500 font-light">Total Build Duration</div>
          </div>
          <div>
            <div className="text-4xl font-light text-white mb-2">6 Phases</div>
            <div className="text-sm text-slate-500 font-light">Sequential Build Stages</div>
          </div>
          <div>
            <div className="text-4xl font-light text-white mb-2">Ready</div>
            <div className="text-sm text-slate-500 font-light">For Operation</div>
          </div>
        </div>
        
        {/* Explore Designs Button moved here */}
        <div className="mt-8">
          <a
            href="https://superyachtevo.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-light"
          >
            <span>EXPLORE DESIGNS</span>
            <SafeIcon icon={FiExternalLink} className="text-sm" />
          </a>
        </div>
      </motion.div>
    </div>
  );
}

export default App;